/**
 * SACP Increment 3 UI — authenticated axios client for the commercial APIs.
 *
 * SuperAdmin (owner) calls carry the OTP session token; tenant calls carry the Firebase ID token.
 * Authorization is enforced by the BACKEND — this client only attaches the caller's real token.
 * There is deliberately NO client-side entitlement decision here.
 */
import { useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export function superAdminToken(superAdmin) {
  return (
    superAdmin?.token ||
    localStorage.getItem('superadmin_session') ||
    localStorage.getItem('superadmin_session_token') ||
    localStorage.getItem('superAdminToken') ||
    localStorage.getItem('superadminToken') ||
    null
  );
}

/** Owner client (OTP session). */
export function useOwnerApi(superAdmin) {
  const ref = useRef(null);
  if (!ref.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token = superAdminToken(superAdmin);
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    ref.current = instance;
  }
  return ref.current;
}

/** Tenant client (Firebase ID token supplied by a getToken() callback). */
export function useTenantApi(getToken) {
  const ref = useRef(null);
  if (!ref.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use(async (config) => {
      try {
        const token = typeof getToken === 'function' ? await getToken() : null;
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (_) { /* unauthenticated -> backend returns 401 */ }
      return config;
    });
    ref.current = instance;
  }
  return ref.current;
}

/** Translate a backend commercial error into a useful message (never a raw stack). */
export function commercialError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const code = (detail && (detail.code || detail)) || null;
  const messages = {
    NOT_ENTITLED_CANNOT_CONFIGURE: 'This capability is not entitled for this tenant.',
    CAPABILITY_NOT_AVAILABLE: 'This capability is not available yet.',
    CONNECTOR_ADMIN_ROLE_REQUIRED: 'You do not have permission to manage connectors.',
    BILLING_ROLE_REQUIRED: 'Only a Billing Administrator may perform this action.',
    ALL_SLOTS_USED: 'All active integration slots are in use.',
    OFFER_NOT_APPROVED: 'This offer is not yet approved.',
    ACTIVATION_REQUIRES_ACCEPTANCE_OR_CONTRACT: 'Activation requires tenant acceptance or a recorded contract.',
    CROSS_TENANT_OFFER_DENIED: 'This offer belongs to a different tenant.',
  };
  if (code && messages[code]) return messages[code];
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You are not authorized to perform this action.';
  if (status === 404) return 'Not found.';
  if (status === 409 && code) return `Not allowed: ${code}`;
  if (status) return `Request failed (HTTP ${status}).`;
  return 'Network error. Please retry.';
}
